import React, { useState } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Empty,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  KeyOutlined,
  ImportOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import type { User, UserListProps, SearchParams } from '../types';
import { DesignTokens, getUserStatusColor } from '../DesignTokens';
import { maskPhone, formatRelativeTime } from '../utils';

const { RangePicker } = DatePicker;
const { Title } = Typography;

export const UserList: React.FC<UserListProps> = ({
  data = [],
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onResetPassword,
  onBatchImport,
  onBatchExport,
  onBatchDisable,
  onSearch,
}) => {
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const handleSearch = () => {
    onSearch?.(searchParams);
  };

  const handleReset = () => {
    setSearchParams({});
    onSearch?.({});
  };

  const handleBatchDisable = () => {
    onBatchDisable?.(selectedRowKeys.map(Number));
    setSelectedRowKeys([]);
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
      sorter: true,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      sorter: true,
    },
    {
      title: '真实姓名',
      dataIndex: 'realName',
      key: 'realName',
      width: 100,
      sorter: true,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      align: 'center',
      render: (phone: string) => (
        <Tooltip title={phone}>
          <span style={{ color: DesignTokens.colors.gray8 }}>
            {maskPhone(phone)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (email?: string) => email || '-',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      width: 180,
      render: (roles: User['roles']) => (
        <Space size={4} wrap>
          {roles.map((role) => (
            <Tag
              key={role.id}
              color="blue"
              style={{
                borderRadius: DesignTokens.borderRadius.sm,
                fontSize: 12,
              }}
            >
              {role.name}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '所属组织',
      dataIndex: 'organizationName',
      key: 'organizationName',
      width: 120,
      ellipsis: true,
      render: (organizationName?: string) => (
        <Tooltip title={organizationName}>
          <span>{organizationName || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: '岗位',
      dataIndex: 'positionName',
      key: 'positionName',
      width: 100,
      render: (positionName?: string) => (
        positionName ? (
          <Tag color="purple" style={{ borderRadius: DesignTokens.borderRadius.sm }}>
            {positionName}
          </Tag>
        ) : '-'
      ),
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      width: 100,
      render: (department?: string) => department || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      sorter: true,
      render: (status: User['status']) => {
        const isActive = status === 'active';
        return (
          <Tag
            color={isActive ? 'success' : 'default'}
            style={{
              borderRadius: DesignTokens.borderRadius.sm,
              fontSize: 14,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getUserStatusColor(status),
                marginRight: 4,
              }}
            />
            {isActive ? '正常' : '禁用'}
          </Tag>
        );
      },
    },
    {
      title: '最后登录',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 140,
      align: 'center',
      sorter: true,
      render: (lastLogin?: string) => (
        <Tooltip title={lastLogin ? dayjs(lastLogin).format('YYYY-MM-DD HH:mm:ss') : ''}>
          <span style={{ color: DesignTokens.colors.gray7 }}>
            {formatRelativeTime(lastLogin)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center',
      sorter: true,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            data-testid={`user-btn-edit-${record.id}`}
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit?.(record.id)}
          >
            编辑
          </Button>
          <Button
            data-testid={`user-btn-reset-password-${record.id}`}
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => onResetPassword?.(record.id)}
          >
            重置密码
          </Button>
          <Button
            data-testid={`user-btn-delete-${record.id}`}
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete?.(record.id)}
          >
            删除
          </Button>
          {record.status === 'active' ? (
            <Button
              data-testid={`user-btn-disable-${record.id}`}
              type="link"
              size="small"
              icon={<LockOutlined />}
              onClick={() => onBatchDisable?.([record.id])}
            >
              禁用
            </Button>
          ) : (
            <Button
              data-testid={`user-btn-enable-${record.id}`}
              type="link"
              size="small"
              icon={<UnlockOutlined />}
              onClick={() => onBatchDisable?.([record.id])}
            >
              启用
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  return (
    <div style={{ padding: DesignTokens.spacing.xl }}>
      {/* 顶部操作栏 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: DesignTokens.spacing.xl,
          height: 56,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, ...DesignTokens.typography.h2 }}>
            用户管理
          </Title>
          <div
            style={{
              ...DesignTokens.typography.caption,
              color: DesignTokens.colors.gray7,
              marginTop: 4,
            }}
          >
            系统管理 / 用户管理
          </div>
        </div>
        <Space>
          <Button
            data-testid="user-btn-import"
            icon={<ImportOutlined />}
            onClick={onBatchImport}
            style={{ height: 32 }}
          >
            批量导入
          </Button>
          <Button
            data-testid="user-btn-export"
            icon={<ExportOutlined />}
            onClick={onBatchExport}
            style={{ height: 32 }}
          >
            批量导出
          </Button>
          <Button
            data-testid="user-btn-add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAdd}
            style={{ height: 32 }}
          >
            新建用户
          </Button>
        </Space>
      </div>

      {/* 筛选栏 */}
      <div
        style={{
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.md}px ${DesignTokens.spacing.xl}px`,
          marginBottom: DesignTokens.spacing.lg,
          borderRadius: DesignTokens.borderRadius.md,
        }}
      >
        <Space size="middle" wrap>
          <div>
            <label
              style={{
                ...DesignTokens.typography.bodyRegular,
                color: DesignTokens.colors.gray8,
                marginRight: 8,
              }}
            >
              用户状态
            </label>
            <Select
              data-testid="user-select-status-filter"
              placeholder="全部"
              style={{ width: 120 }}
              value={searchParams.status}
              onChange={(value) =>
                setSearchParams({ ...searchParams, status: value })
              }
              options={[
                { label: '全部', value: undefined },
                { label: '正常', value: 'active' },
                { label: '禁用', value: 'disabled' },
              ]}
            />
          </div>
          <div>
            <label
              style={{
                ...DesignTokens.typography.bodyRegular,
                color: DesignTokens.colors.gray8,
                marginRight: 8,
              }}
            >
              创建时间
            </label>
            <RangePicker
              data-testid="user-picker-date-range"
              style={{ width: 240 }}
              value={
                searchParams.dateRange
                  ? [
                      dayjs(searchParams.dateRange[0]),
                      dayjs(searchParams.dateRange[1]),
                    ]
                  : null
              }
              onChange={(dates) =>
                setSearchParams({
                  ...searchParams,
                  dateRange: dates
                    ? [dates[0]!.toISOString(), dates[1]!.toISOString()]
                    : undefined,
                })
              }
            />
          </div>
          <Input
            data-testid="user-input-search"
            placeholder="搜索用户名/姓名/手机号"
            prefix={<SearchOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            style={{ width: 300 }}
            value={searchParams.keyword}
            onChange={(e) =>
              setSearchParams({ ...searchParams, keyword: e.target.value })
            }
            onPressEnter={handleSearch}
          />
          <Button data-testid="user-btn-search" type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button data-testid="user-btn-reset" onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 批量操作栏 */}
      {selectedRowKeys.length > 0 && (
        <div
          style={{
            background: DesignTokens.colors.primaryLight + '15',
            padding: `${DesignTokens.spacing.sm}px ${DesignTokens.spacing.lg}px`,
            marginBottom: DesignTokens.spacing.lg,
            borderRadius: DesignTokens.borderRadius.md,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ ...DesignTokens.typography.bodyRegular }}>
            已选择 <strong>{selectedRowKeys.length}</strong> 项
          </span>
          <Space>
            <Button data-testid="user-btn-deselect" size="small" onClick={() => setSelectedRowKeys([])}>
              取消选择
            </Button>
            <Button
              data-testid="user-btn-batch-disable"
              size="small"
              danger
              icon={<LockOutlined />}
              onClick={handleBatchDisable}
            >
              批量禁用
            </Button>
          </Space>
        </div>
      )}

      {/* 数据表格 */}
      <Table
        data-testid="user-table"
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        rowSelection={rowSelection}
        scroll={{ x: 1600 }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无用户数据"
              style={{ padding: '60px 0' }}
            >
              <Button data-testid="user-btn-add-empty" type="primary" icon={<PlusOutlined />} onClick={onAdd}>
                创建第一个用户
              </Button>
            </Empty>
          ),
        }}
        style={{
          background: DesignTokens.colors.gray1,
          borderRadius: DesignTokens.borderRadius.lg,
        }}
      />
    </div>
  );
};
